import React from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { Button } from '@host/components/NewUI/ComponentsLibrary';

import PortalPreviewFrame from './PortalPreviewFrame';
import { onboardColors } from '../../theme/onboardTokens';
import { submittedAssetUrl } from '../api';
import { font, palette } from '../styles';
import { triggerDownload } from '../usePortalDownloads';

import type { IPortalAssetFile } from '../types';

interface Props {
  token: string;
  files: IPortalAssetFile[];
}

const PREVIEWABLE_IMAGES = ['image/jpeg', 'image/png'];

const SubmittedFile: React.FC<{ token: string; file: IPortalAssetFile }> = ({ token, file }) => {
  const url = submittedAssetUrl(token, file.id);
  const fileName = file.file_name ?? 'Submitted document';

  if (file.content_type === 'application/pdf') {
    return <PortalPreviewFrame fileName={fileName} src={url} />;
  }
  if (file.content_type && PREVIEWABLE_IMAGES.includes(file.content_type)) {
    return <PortalPreviewFrame fileName={fileName} kind="image" src={url} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        borderRadius: 8,
        background: palette.uploadBg
      }}
    >
      <InsertDriveFileOutlinedIcon sx={{ color: onboardColors.neutral.icon }} />
      <div style={{ flex: 1, minWidth: 160 }}>
        <div
          style={{
            fontFamily: font,
            fontSize: 14,
            fontWeight: 600,
            color: onboardColors.neutral.textStrong,
            overflowWrap: 'anywhere'
          }}
        >
          {fileName}
        </div>
        <div style={{ fontFamily: font, fontSize: 12, color: palette.bodyNeutral }}>
          Preview isn&apos;t available for this file type.
        </div>
      </div>
      <Button
        buttonVariant="secondary"
        icon={<FileDownloadIcon />}
        id={`portal-download-submitted-document-${file.id}`}
        onClick={() => triggerDownload(url, fileName)}
      >
        Download
      </Button>
    </div>
  );
};

/**
 * The candidate's own uploads for a submitted document item, oldest first. PDFs and
 * images preview inline; Word files can't render in a browser, so they get a download.
 */
const SubmittedDocumentPreview: React.FC<Props> = ({ token, files }) => (
  <section aria-label="Your submitted document">
    <p
      style={{
        fontFamily: font,
        fontSize: 16,
        color: palette.black,
        margin: '24px 0 12px'
      }}
    >
      {files.length > 1 ? 'Your submitted documents' : 'Your submitted document'}
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {files.map((file) => (
        <SubmittedFile key={file.id} file={file} token={token} />
      ))}
    </div>
  </section>
);

export default SubmittedDocumentPreview;
